import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Form, Modal } from "react-bootstrap";
import {
  fetchDeliveryProfiles,
  deleteDeliveryProfile,
  approveDeliveryProfile,
  clearError,
  clearSuccess,
} from "../../redux/slices/deliveryProfileSlice";
import InfoPill from "../Common/InfoPill";
import { toast } from "react-toastify";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";

const DeliveryProfiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    deliveryProfiles = [],
    isLoading,
    error,
    success,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.deliveryProfiles);

  const [selectedDeliveryProfiles, setSelectedDeliveryProfiles] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deliveryProfileToDelete, setDeliveryProfileToDelete] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [deliveryProfileToApprove, setDeliveryProfileToApprove] =
    useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");

  useEffect(() => {
    const params = {};
    if (statusFilter) params.is_active = statusFilter;
    if (cityFilter) params.city = cityFilter;
    dispatch(fetchDeliveryProfiles(params));
  }, [dispatch, statusFilter, cityFilter, success]);

  // Show toast messages for success and error
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 1000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 1000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const handleCreateDeliveryProfile = () => {
    navigate("/admin/delivery-profiles/create");
  };

  const handleEditDeliveryProfile = (deliveryProfile) => {
    navigate(`/admin/delivery-profiles/edit/${deliveryProfile.id}`);
  };

  const handleDeleteDeliveryProfile = (deliveryProfile) => {
    setDeliveryProfileToDelete(deliveryProfile);
    setShowDeleteModal(true);
  };

  const handleApproveDeliveryProfile = (deliveryProfile) => {
    setDeliveryProfileToApprove(deliveryProfile);
    setShowApproveModal(true);
  };

  const confirmApprove = () => {
    if (deliveryProfileToApprove) {
      dispatch(approveDeliveryProfile(deliveryProfileToApprove.id));
      setShowApproveModal(false);
      setDeliveryProfileToApprove(null);
    }
  };

  const confirmDelete = () => {
    if (deliveryProfileToDelete) {
      dispatch(deleteDeliveryProfile(deliveryProfileToDelete.id));
      setShowDeleteModal(false);
      setDeliveryProfileToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedDeliveryProfiles.length === 0) {
      toast.warning("Please select delivery profiles to delete");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedDeliveryProfiles.length} delivery profiles?`
      )
    ) {
      selectedDeliveryProfiles.forEach((profileId) => {
        dispatch(deleteDeliveryProfile(profileId));
      });
      setSelectedDeliveryProfiles([]);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Inactive</Badge>
    );
  };

  // Helper function to convert date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if already in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Check if in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }

    // Check if in DD/MM/YYYY format (backward compatibility)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString.replace(/\//g, "-");
    }

    // Try to parse as Date and convert (avoid timezone issues)
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Use local date components to avoid timezone shift
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    return dateString;
  };

  const tableHeaders = [
    {
      prop: "full_name",
      title: "Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Link
          to={`/admin/delivery-profiles/details/${row.id}`}
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
        >
          {row.full_name}
        </Link>
      ),
    },
    {
      prop: "email",
      title: "Email",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "phone",
      title: "Phone",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "city",
      title: "City",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      cell: (row) => getStatusBadge(row.is_active),
    },
    {
      prop: "approval",
      title: "Approval",
      isSortable: true,
      cell: (row) => {
        // Handle null/undefined is_verified (default to false/pending)
        const isVerified =
          row.is_verified === true || row.is_verified === "true";
        return isVerified ? (
          <Badge bg="success">Approved</Badge>
        ) : (
          <Badge bg="warning">Pending</Badge>
        );
      },
    },
    {
      prop: "hire_date",
      title: "Hire Date",
      isSortable: true,
      cell: (row) => formatDate(row.hire_date),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => {
        // Handle null/undefined is_verified (show approve button if not explicitly true)
        const isVerified =
          row.is_verified === true || row.is_verified === "true";
        return (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="success"
              onClick={() => handleApproveDeliveryProfile(row)}
              disabled={isUpdating || isVerified}
              title={isVerified ? "Already approved" : "Approve delivery partner"}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={() => handleEditDeliveryProfile(row)}
              title="Edit"
            >
              <FaPencilAlt />
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDeleteDeliveryProfile(row)}
              disabled={isDeleting}
              title="Delete"
            >
              <FaRegTrashAlt />
            </Button>
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return <div className="text-center mt-4">Loading delivery profiles...</div>;
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Delivery Profiles
        </Link>
      </div>

      <DatatableWrapper
        body={Array.isArray(deliveryProfiles) ? deliveryProfiles : []}
        headers={tableHeaders}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="d-flex align-items-left mb-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search delivery profiles..."
                className="w-100 btn-success"
              />
            </div>

            {/* Status Filter */}
            <div style={{ maxWidth: "150px" }}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill
              left="Total Profiles"
              right={deliveryProfiles?.length || 0}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            {selectedDeliveryProfiles.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                variant="danger"
                className="btn-sm"
                disabled={isDeleting}
              >
                Delete Selected ({selectedDeliveryProfiles.length})
              </Button>
            )}
            <Button
              onClick={handleCreateDeliveryProfile}
              variant="success"
              className="btn-sm btn-success"
              disabled={isLoading}
            >
              New Profile
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete delivery profile{" "}
          <strong>
            {deliveryProfileToDelete?.first_name}{" "}
            {deliveryProfileToDelete?.last_name}
          </strong>
          ? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal
        show={showApproveModal}
        onHide={() => {
          setShowApproveModal(false);
          setDeliveryProfileToApprove(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve Delivery Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to approve delivery partner{" "}
          <strong>
            {deliveryProfileToApprove?.first_name}{" "}
            {deliveryProfileToApprove?.last_name}
          </strong>
          ? They will be able to login after approval.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowApproveModal(false);
              setDeliveryProfileToApprove(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmApprove}
            disabled={isUpdating}
          >
            {isUpdating ? "Approving..." : "Approve"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DeliveryProfiles;
